import { useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { isWeekend } from 'date-fns'
import { Book, Rewind } from 'lucide-react'

import { getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { DatePickerWithControls } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { OPERATIONS } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { NtHedgingFxFwdTable } from '@/pages/operations/nt-hedging-fwd/components/NtHedgingFxFwdTable'
import { NtMaturingFxFwdTable } from '@/pages/operations/nt-hedging-fwd/components/NtMaturingFxFwdTable'
import { useBookHedgingMutation, useUnwindMaturingMutation } from '@/react-query/mutations/hedging'
import { useFunds } from '@/react-query/queries/funds/funds'

export interface NtHedgingFwdPageProps {}

export const NtHedgingFwdPage: React.FC<NtHedgingFwdPageProps> = ({}) => {
  const [fund_name, setFundName] = useState('-')

  const [value_date, setValueDate] = useState(getTMinus1())

  const bookHedgingMutation = useBookHedgingMutation()
  const unwindMaturingMutation = useUnwindMaturingMutation()

  const params = useMemo(() => {
    const _return: { value_date: string; fund_name?: string } = {
      value_date: toISO8601(value_date),
    }
    if (fund_name) _return.fund_name = fund_name
    return _return
  }, [value_date, fund_name])

  const { Dialog: BookDialog, withConfirmation: confirmBook } = useConfirmModal({
    title: `Book ${params.value_date}${fund_name !== '-' ? ` | ${fund_name}` : ''}`,
    description: `You are about to book trades on ${params.value_date}${fund_name !== '-' ? ` for ${fund_name}.` : '.'} Continue?`,
  })

  const { Dialog: UnwindDialog, withConfirmation: confirmUnwind } = useConfirmModal({
    title: `Unwind for ${params.value_date}${fund_name !== '-' ? ` | ${fund_name}` : ''}`,
    description: `You are about to unwind trades on ${params.value_date}${fund_name !== '-' ? ` for ${fund_name}.` : '.'} Continue?`,
  })

  const { data: funds, query: fundsQuery } = useFunds()

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['hedging', 'fxfwd', { value_date }],
        queryFn: () => api.hedging.getHedging({ value_date: params.value_date }),
        refetchInterval: 30_000,
        enabled: funds.length > 0,
      },
      {
        queryKey: ['hedging', 'maturing', 'fxfwd', { value_date }],
        queryFn: () => api.hedging.getMaturing({ value_date: params.value_date }),
        refetchInterval: 15_000,
        enabled: funds.length > 0,
      },
    ],
  })

  const [hedging, maturing] = queries

  const queriesAreFetching = [fundsQuery, ...queries].some((query) => query.isFetching)
  const mutationsArePending = [bookHedgingMutation, unwindMaturingMutation].some(
    (mutation) => mutation.isPending,
  )
  const inputsDisabled = queriesAreFetching || mutationsArePending

  const filterData = <TItem extends { fund_name: string }>(data: TItem[], fund_name: string) => {
    return fund_name === '-' ? data : data.filter((item) => item.fund_name === fund_name)
  }

  const hasDataForFund = <TItem extends { fund_name: string }>(
    data: TItem[],
    fund_name: string,
  ) => {
    return filterData(data, fund_name).length > 0
  }

  const fundsOptions = useMemo(() => {
    const data = [...(hedging.data ?? []), ...(maturing.data ?? [])]
    return funds
      .filter((fund) => hasDataForFund(data, fund.fund_name))
      .map((fund) => ({ label: fund.fund_name, value: fund.fund_name }))
  }, [funds, hedging.data])

  const filteredHedging = useMemo(() => {
    return filterData(hedging.data ?? [], fund_name)
  }, [hedging.data, fund_name])

  const filteredMaturing = useMemo(() => {
    return filterData(maturing.data ?? [], fund_name)
  }, [maturing.data, fund_name])

  const hasHedgingDataForSelectedDate = filteredHedging.length > 0
  const hasMaturingDataForSelectedDate = filteredMaturing.length > 0

  return (
    <Page
      metadata={{ title: 'NT Hedging Fwd' }}
      permissions={OPERATIONS.NT_HEDGING_FWD.permissions}
    >
      <CanAccess permissions={[PERMISSIONS.WRITE_NT_HEDGING_FXFWD]}>
        <BookDialog />
        <UnwindDialog />
      </CanAccess>
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <FormItem>
            <Label className={'text-xs'}>Report Date</Label>
            <DatePickerWithControls
              size="sm"
              selected={value_date}
              buttonDisabled={inputsDisabled}
              disabled={(date) => isWeekend(date)}
              onSelect={(date) => date && setValueDate(date)}
              todayButton={{ enabled: true, text: 'T - 1', date: getTMinus1() }}
            />
          </FormItem>
          <FormItem>
            <Label className={'text-xs'}>Fund</Label>
            <SearchSelect
              size="sm"
              placeholder={'Filter by fund'}
              className={'min-w-auto w-40'}
              value={fund_name}
              setValue={setFundName}
              options={[{ value: '-', label: 'All' }, ...fundsOptions]}
            />
          </FormItem>
          <CanAccess permissions={[PERMISSIONS.WRITE_NT_HEDGING_FXFWD]}>
            <div className="flex h-full">
              <div className="mt-auto flex items-center gap-2">
                <LoadingButton
                  size="sm"
                  variant={'outline'}
                  disabled={!hasHedgingDataForSelectedDate}
                  onClick={async (_e) => confirmBook(() => bookHedgingMutation.mutateAsync(params))}
                  icon={<Book />}
                >
                  Book visible
                </LoadingButton>
                <LoadingButton
                  size="sm"
                  variant={'outline'}
                  disabled={!hasMaturingDataForSelectedDate}
                  onClick={async (_e) =>
                    confirmUnwind(() => unwindMaturingMutation.mutateAsync(params))
                  }
                  icon={<Rewind />}
                >
                  Unwind visible
                </LoadingButton>
              </div>
            </div>
          </CanAccess>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'vertical'}>
        <ResizablePanel order={1} id="panel-new" minSize={5} collapsible={true} collapsedSize={1}>
          <Widget className={'h-full'} exposed>
            <WidgetHeaderWithQuery title={`New - NT Hedging FX FWD`} query={hedging} />
            <WidgetContent loading={hedging.isLoading}>
              <NtHedgingFxFwdTable query={hedging} domLayout={'normal'} rowData={filteredHedging} />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel order={2} is="panel-maturing" minSize={5}>
          <Widget className={'h-full'} exposed>
            <WidgetHeaderWithQuery title={`Maturing - NT Hedging FX FWD`} query={maturing} />
            <WidgetContent loading={maturing.isLoading}>
              <NtMaturingFxFwdTable
                query={maturing}
                domLayout={'normal'}
                rowData={filteredMaturing}
              />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
      </ResizablePanelGroup>
    </Page>
  )
}
